import {
  ALL_ALCHEMY_TYPES, ALL_BODY_GENES, ALL_EAR_GENES, ALL_HORN_GENES,
  ALL_MARKING_GENES, ALL_TAIL_GENES,
  type AlchemyType, type BodyGene, type EarGene, type HornGene,
  type MarkingGene, type TailGene,
} from '@/domain/genetics/types';
import type { WildEnemyRuntime } from '@/domain/combat/types';
import { ALL_BIOMES, type BiomeId } from './types';

export interface WildDiscovery {
  biome: BiomeId;
  encounters: number;
  dominantSeen: boolean;
  bodies: BodyGene[];
  ears: EarGene[];
  horns: HornGene[];
  tails: TailGene[];
  markings: MarkingGene[];
  types: AlchemyType[];
}

export type WildBestiary = Record<string, WildDiscovery>;

export function recordWildDiscovery(current: WildBestiary, enemy: WildEnemyRuntime): WildBestiary {
  const before = current[enemy.populationId];
  const genome = enemy.genome;
  const entry: WildDiscovery = {
    biome: enemy.biome,
    encounters: (before?.encounters ?? 0) + 1,
    dominantSeen: (before?.dominantSeen ?? false) || enemy.rank === 'dominant',
    bodies: sortedUnique([...(before?.bodies ?? []), genome.body]),
    ears: sortedUnique([...(before?.ears ?? []), genome.ears]),
    horns: sortedUnique([...(before?.horns ?? []), genome.horns]),
    tails: sortedUnique([...(before?.tails ?? []), genome.tail]),
    markings: sortedUnique([...(before?.markings ?? []), genome.markings]),
    types: sortedUnique([...(before?.types ?? []), ...genome.primaryTypes, enemy.type]),
  };
  return { ...current, [enemy.populationId]: entry };
}

export function normalizeWildBestiary(value: unknown): WildBestiary {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return {};
  const output: WildBestiary = {};
  for (const [id, raw] of Object.entries(value)) {
    if (!raw || typeof raw !== 'object' || Array.isArray(raw)) continue;
    const item = raw as Record<string, unknown>;
    if (!ALL_BIOMES.includes(item.biome as BiomeId)) continue;
    output[id] = {
      biome: item.biome as BiomeId,
      encounters: Math.max(0, Math.floor(typeof item.encounters === 'number' ? item.encounters : 0)),
      dominantSeen: item.dominantSeen === true,
      bodies: legalValues(item.bodies, ALL_BODY_GENES),
      ears: legalValues(item.ears, ALL_EAR_GENES),
      horns: legalValues(item.horns, ALL_HORN_GENES),
      tails: legalValues(item.tails, ALL_TAIL_GENES),
      markings: legalValues(item.markings, ALL_MARKING_GENES),
      types: legalValues(item.types, ALL_ALCHEMY_TYPES),
    };
  }
  return output;
}

function legalValues<T extends string>(value: unknown, legal: readonly T[]): T[] {
  if (!Array.isArray(value)) return [];
  return sortedUnique(value.filter((entry): entry is T => typeof entry === 'string' && legal.includes(entry as T)));
}

function sortedUnique<T extends string>(values: readonly T[]): T[] {
  return [...new Set(values)].sort((a, b) => a.localeCompare(b));
}
